import { Badge, Button, Card, CardContent, Skeleton, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { FlagRow } from '../pricing-settings/billing-form-fields.js';

type Status = 'ok' | 'failed' | 'skipped';

interface IntegrationResult {
  key: string;
  label: string;
  status: Status;
  httpStatus?: number;
  message: string;
  checkedAt: string;
}

interface HealthResponse {
  enabled: boolean;
  checkedAt: string | null;
  results: IntegrationResult[];
}

const HEALTH_KEY = ['admin-integrations-health'];

const STATUS_CHIP: Record<Status, { label: string; variant: 'success' | 'danger' | 'default' }> = {
  ok: { label: 'Funcionando', variant: 'success' },
  failed: { label: 'Com problema', variant: 'danger' },
  skipped: { label: 'Não configurada', variant: 'default' },
};

const formatDateTime = (raw: string) =>
  new Date(raw).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Daily check of third-party credentials (Asaas, e-mail, SMS, captcha, placas).
 * Read-only probes on the API side; this card only shows the last run.
 */
export const IntegrationsHealthCard = () => {
  const qc = useQueryClient();
  const health = useQuery({
    queryKey: HEALTH_KEY,
    queryFn: () => api.request<HealthResponse>('/v1/admin/integrations/health'),
  });

  const runNow = useMutation({
    mutationFn: () =>
      api.request<HealthResponse>('/v1/admin/integrations/health/run', { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(HEALTH_KEY, data);
      const failed = data.results.filter((r) => r.status === 'failed').length;
      if (failed > 0) toast.error(`${failed} integração(ões) com problema.`);
      else toast.success('Verificação concluída. Nenhum problema encontrado.');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível verificar agora.'),
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.request('/v1/admin/settings/flags', {
        method: 'PUT',
        json: { credentialCanaryEnabled: enabled },
      }),
    onSuccess: () => {
      toast.success('Verificação diária atualizada.');
      qc.invalidateQueries({ queryKey: HEALTH_KEY });
      qc.invalidateQueries({ queryKey: ['admin-settings-flags'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const data = health.data;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold">Integrações</h2>
            <p className="text-xs text-muted-fg">
              {data?.checkedAt
                ? `Última verificação: ${formatDateTime(data.checkedAt)}`
                : 'Ainda não verificado.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full shrink-0 sm:w-auto"
            disabled={runNow.isPending}
            onClick={() => runNow.mutate()}
          >
            {runNow.isPending ? 'Verificando...' : 'Verificar agora'}
          </Button>
        </div>

        {health.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : health.isError ? (
          <p className="text-sm text-muted-fg">
            Não foi possível carregar. Tente recarregar a página.
          </p>
        ) : !data || data.results.length === 0 ? (
          <p className="text-sm text-muted-fg">
            Clique em "Verificar agora" para testar as chaves das integrações.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.results.map((r) => (
              <li key={r.key} className="flex flex-col gap-1 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm">{r.label}</span>
                  <Badge variant={STATUS_CHIP[r.status].variant} className="shrink-0">
                    {STATUS_CHIP[r.status].label}
                  </Badge>
                </div>
                {r.status !== 'ok' && <p className="text-xs text-muted-fg">{r.message}</p>}
              </li>
            ))}
          </ul>
        )}

        {data && (
          <FlagRow
            label="Verificação diária"
            hint="Testa as chaves uma vez por dia, sem enviar nada, e avisa por e-mail se algo parar de funcionar"
            enabled={data.enabled}
            pending={toggle.isPending}
            onToggle={(v) => toggle.mutate(v)}
          />
        )}
      </CardContent>
    </Card>
  );
};
